/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStorage } from '@/hooks/use-storage';
import { BASE_PATH, makeFullPath } from '@/utils/path-utils';

const TOKEN_KEY = 'session_token';

// TEMPORARY: Hardcode a session for development to bypass auth context.
export const useDevAuth = () => ({
  session: {
    userId: 'dev-user-id-12345',
    email: 'dev@spark.com',
  },
});

interface Session {
  token: string;
  userId: string;
  email: string;
}

interface AuthContextType {
  signIn: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  session: Session | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const storage = useStorage();

  // Load session on app start - but don't auto-login
  useEffect(() => {
    const loadSession = async () => {
      try {
        // For now, we'll start with no session by default
        // Users must explicitly sign in
        setSession(null);
      } catch (e) {
        console.error('Failed to load session', e);
      } finally {
        setIsLoading(false);
      }
    };

    void loadSession();
  }, []);

  // Handle routing based on authentication state
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const hasAuthId = searchParams.has('auth_id');
    // All auth-related paths are now under /idp
    const inAuthGroup = pathname.startsWith(BASE_PATH);
    const isRoot = pathname === '/';

    if (
      session &&
      inAuthGroup &&
      !pathname.startsWith(makeFullPath('/onboarding'))
    ) {
      // User is authenticated but on auth pages (not onboarding), redirect to main
      navigate(BASE_PATH);
    } else if (!session && !inAuthGroup && !isRoot) {
      // User is not authenticated and not on auth pages
      // If auth_id is present, redirect to auth flow, otherwise to login
      const redirectPath = hasAuthId ? '/' : '/login';
      navigate(makeFullPath(`${redirectPath}${location.search}`)); // Preserve search params
    } else if (!session && isRoot) {
      // Redirect root to login
      navigate(makeFullPath('/login'));
    }
  }, [session, location.pathname, location.search, isLoading, navigate]);

  const authValue: AuthContextType = {
    signIn: async (email: string) => {
      // Create a simple session with the email
      const mockSession: Session = {
        token: email, // Using email as token for simplicity
        userId: 'user-123',
        email: email,
      };
      await storage.setItem(TOKEN_KEY, email);
      setSession(mockSession);
    },
    signOut: async () => {
      await storage.removeItem(TOKEN_KEY);
      setSession(null);
    },
    session,
    isLoading,
  };

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
}
